/*
 * BTU Load Calculator — Wholesale Beer Parts
 *
 * Deployed to the live theme as assets/btu-calculator.js and loaded from
 * layout/theme.liquid. Powers the calculator on /pages/btu-load-calculator.
 *
 * Auto-discovers glycol power packs from the "Glycol Chillers & Long Draw
 * Cooling Systems" collection (COLLECTION_HANDLE below) via Shopify's public
 * /collections/{handle}/products.json endpoint — no code edit needed when
 * Sean adds a new chiller to that collection. That collection also holds
 * pumps, motors, thermostats, duct fittings and glycol concentrate, so a
 * product only counts as a power pack if its title contains the literal
 * phrase "Power Pack" (case-insensitive) — that phrase is the one signal
 * that's consistent across every real chiller and no accessory. BTU/HP/pump
 * count are parsed from the title too (e.g. "...7,250 BTU - 1 HP - 2 Pumps...").
 *
 * IMPORTANT for whoever names the next product: keep "Power Pack" in the
 * title and a "<number> BTU" figure somewhere in it, or this parser will
 * silently skip it (it warns to the console, but the page won't break —
 * silent skip is the safer failure mode than silently including junk).
 *
 * FALLBACK_PACKS is a small hand-verified list used only if the live fetch
 * fails (network issue, endpoint blocked, etc.) so the calculator never goes
 * blank. It will not reflect new products — the live fetch is what does.
 *
 * Below the recommendation, the page also renders a full "browse all tiers"
 * table (#allTiersBox) — every pack we sell, grouped by BTU, including
 * remote and water-cooled variants (that table is informational, unlike the
 * recommendation logic which always excludes water-cooled). Gaps versus
 * Micro Matic's full Pro-Line lineup are tracked in
 * docs/micro-matic-power-pack-checklist.md.
 */
(function () {
  "use strict";

  var COLLECTION_HANDLE = "glycol-chillers-long-draw-cooling-systems";
  var COLLECTION_JSON_URL = "/collections/" + COLLECTION_HANDLE + "/products.json?limit=250";
  var COLLECTION_URL = "/collections/" + COLLECTION_HANDLE;
  var CONTACT_URL = "/pages/contact";

  // Preferred brand for the headline recommendation. Falls back to any
  // vendor when no Micro Matic Pro-Line pack covers the load.
  var PREFERRED_VENDOR = "Micro Matic";

  // Load per faucet, and the safety factor applied to the raw total.
  var BTU_PER_FAUCET = 100;
  var SAFETY_FACTOR = 1.4;

  // Last-resort static list — only used if the live collection fetch fails.
  // Verified active 2026-08-11. Will not include anything added after that
  // date; that's the live fetch's job.
  var FALLBACK_PACKS = [
    {
      handle: "pro-line-power-pack-glycol-beer-cooling-system-1700-btu-14-hp-1-pump-air-cooled",
      title: "Pro-Line™ Power Pack — 1,700 BTU — 1/4 HP — 1 Pump — Air-Cooled",
      btu: 1700,
      hp: "1/4 HP",
      pumps: 1,
      price: 155183,
      vendor: "Micro Matic",
      tier: "standard",
      cooling: "air-cooled"
    },
    {
      handle: "pro-line-power-pack-2300-btu-13-hp-1-pump-air-cooled",
      title: "Pro-Line™ Power Pack — 2,300 BTU — 1/3 HP — 1 Pump — Air Cooled",
      btu: 2300,
      hp: "1/3 HP",
      pumps: 1,
      price: 293693,
      vendor: "Micro Matic",
      tier: "standard",
      cooling: "air-cooled"
    },
    {
      handle: "chill-max-13-hp-single-pump-glycol-power-pack-2560-btus",
      title: "Chill-Max 1/3 HP Single Pump Glycol Power Pack, 2560 BTU's",
      btu: 2560,
      hp: "1/3 HP",
      pumps: 1,
      price: 179500,
      vendor: "Chill-Max",
      tier: "standard",
      cooling: "air-cooled"
    },
    {
      handle: "chill-max-13-hp-single-pump-glycol-power-pack-3282-btus",
      title: "Chill-Max 1/3 HP Single Pump Glycol Power Pack, 3282 BTU's",
      btu: 3282,
      hp: "1/3 HP",
      pumps: 1,
      price: 239500,
      vendor: "Chill-Max",
      tier: "standard",
      cooling: "air-cooled"
    },
    {
      handle: "pro-line-power-pack-3600-btu-12-hp-1-pump-air-cooled",
      title: "Pro-Line™ Power Pack — 3,600 BTU — 1/2 HP — 1 Pump — Air Cooled",
      btu: 3600,
      hp: "1/2 HP",
      pumps: 1,
      price: 334733,
      vendor: "Micro Matic",
      tier: "standard",
      cooling: "air-cooled"
    },
    {
      handle: "chill-max-12-hp-single-pump-glycol-power-pack-4080-btus",
      title: "Chill-Max 1/2 HP Single Pump Glycol Power Pack, 4080 BTU's",
      btu: 4080,
      hp: "1/2 HP",
      pumps: 1,
      price: 295000,
      vendor: "Chill-Max",
      tier: "standard",
      cooling: "air-cooled"
    },
    {
      handle: "pro-line-power-pack-5100-btu-34-hp-2-pumps-air-cooled",
      title: "Pro-Line™ Power Pack — 5,100 BTU — 3/4 HP — 2 Pumps — Air Cooled",
      btu: 5100,
      hp: "3/4 HP",
      pumps: 2,
      price: 490770,
      vendor: "Micro Matic",
      tier: "standard",
      cooling: "air-cooled"
    },
    {
      handle: "chill-max-34-hp-double-pump-glycol-power-pack-6138-btus",
      title: "Chill-Max 3/4 HP Double Pump Glycol Power Pack, 6138 BTU's",
      btu: 6138,
      hp: "3/4 HP",
      pumps: 2,
      price: 405000,
      vendor: "Chill-Max",
      tier: "standard",
      cooling: "air-cooled"
    },
    {
      handle: "pro-line-power-pack-7250-btu-1-hp-2-pumps-air-cooled",
      title: "Pro-Line™ Power Pack — 7,250 BTU — 1 HP — 2 Pumps — Air Cooled",
      btu: 7250,
      hp: "1 HP",
      pumps: 2,
      price: 665655,
      vendor: "Micro Matic",
      tier: "standard",
      cooling: "air-cooled"
    },
    {
      handle: "pro-line-remote-power-pack-11500-btu-1-12-hp-3-pumps-air-cooled",
      title: "Pro-Line™ Remote Power Pack — 11,500 BTU — 1 1/2 HP — 3 Pumps — Air Cooled",
      btu: 11500,
      hp: "1 1/2 HP",
      pumps: 3,
      price: 947768,
      vendor: "Micro Matic",
      tier: "remote",
      cooling: "air-cooled"
    }
  ];

  function formatMoney(cents) {
    if (typeof cents !== "number" || isNaN(cents)) {
      return "";
    }
    return "$" + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  function formatNumber(value) {
    return Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  function productUrl(handle) {
    return "/products/" + handle;
  }

  function isPowerPack(title) {
    return /power\s*pack/i.test(title);
  }

  function parseBTU(title) {
    var m = title.match(/([\d,]+)\s*BTU/i);
    if (!m) {
      return null;
    }
    var n = parseInt(m[1].replace(/,/g, ""), 10);
    return isNaN(n) ? null : n;
  }

  function parseHP(title) {
    var m = title.match(/(\d+(?:\s+\d+\/\d+)?|\d+\/\d+)\s*HP/i);
    return m ? m[1] + " HP" : null;
  }

  function parsePumps(title) {
    // Check word forms first — "Single/Double Pump" never has a stray digit
    // (like an HP fraction: "1/3 HP") sitting near the word "Pump" to confuse.
    if (/double\s*pump/i.test(title)) {
      return 2;
    }
    if (/single\s*pump/i.test(title)) {
      return 1;
    }

    // "N Pump(s)" — the digit must sit immediately next to the word, not just
    // somewhere nearby, or an HP fraction like "1/3 HP ... Pump" elsewhere in
    // the title gets mistaken for the pump count.
    var m = title.match(/(\d+)\s+pumps?\b/i);
    if (m) {
      return parseInt(m[1], 10);
    }

    // "N vertical/horizontal pump" — one qualifier word is allowed here.
    m = title.match(/(\d+)\s+(?:vertical|horizontal)\s+pumps?\b/i);
    return m ? parseInt(m[1], 10) : null;
  }

  function isWaterCooled(title) {
    return /water[\s-]*cooled/i.test(title);
  }

  function isRemote(title) {
    return /\bremote\b/i.test(title);
  }

  /*
   * Shopify's classic /collections/{handle}/products.json ("Ajax API")
   * returns variant.price as a dollar string ("19.99"), NOT cents — unlike
   * /products/{handle}.js which uses cents. Convert once here so every
   * formatMoney() call downstream can assume cents consistently.
   */
  function parsePriceCents(variant) {
    if (!variant || typeof variant.price !== "string") {
      return null;
    }
    var dollars = parseFloat(variant.price);
    return isNaN(dollars) ? null : Math.round(dollars * 100);
  }

  function parseImage(product) {
    var img = product.images && product.images[0];
    if (!img || !img.src) {
      return null;
    }
    return img.src.indexOf("//") === 0 ? "https:" + img.src : img.src;
  }

  /*
   * Turn one product.json entry into a pack object, or null if it's not a
   * sellable power pack (an accessory, a sold-out variant, or a title we
   * can't parse a BTU rating from).
   */
  function toPack(product) {
    if (!isPowerPack(product.title)) {
      return null;
    }

    var variant = product.variants && product.variants[0];
    if (!variant || variant.available === false) {
      return null;
    }

    var btu = parseBTU(product.title);
    if (btu === null) {
      if (window.console && window.console.warn) {
        window.console.warn(
          "[BTU calculator] skipping product — no parseable BTU in title: " + product.title
        );
      }
      return null;
    }

    var priceCents = parsePriceCents(variant);

    return {
      handle: product.handle,
      title: product.title,
      btu: btu,
      hp: parseHP(product.title),
      pumps: parsePumps(product.title),
      price: priceCents,
      vendor: product.vendor,
      image: parseImage(product),
      // "standard" = the on-site air-cooled units Sean wants recommended by
      // default. "remote" = split-deck units (compressor lives elsewhere,
      // e.g. outdoors) — only used when no standard pack covers the load.
      tier: isRemote(product.title) ? "remote" : "standard",
      // Water-cooled units are kept in the full pack list (for the browse-all
      // tiers table) but excluded from recommendations in adequatePacks() —
      // never recommended, on any surface, at any load, per Sean's policy.
      cooling: isWaterCooled(product.title) ? "water-cooled" : "air-cooled"
    };
  }

  var packsPromise = null;

  /* Fetches the collection once per page load; cached for re-calculations. */
  function getPacks() {
    if (packsPromise) {
      return packsPromise;
    }

    if (typeof window.fetch !== "function") {
      packsPromise = Promise.resolve(FALLBACK_PACKS);
      return packsPromise;
    }

    packsPromise = window
      .fetch(COLLECTION_JSON_URL, { credentials: "same-origin" })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      })
      .then(function (data) {
        if (!data || !data.products || !data.products.length) {
          return FALLBACK_PACKS;
        }

        var packs = [];
        data.products.forEach(function (product) {
          var pack = toPack(product);
          if (pack && typeof pack.price === "number") {
            packs.push(pack);
          }
        });

        if (!packs.length) {
          return FALLBACK_PACKS;
        }

        // Sort by BTU, then price — the collection feed's order isn't
        // guaranteed, and several BTU ratings have two variants (single vs
        // double pump). A price tiebreak makes the cheaper of an equal-BTU
        // pair win deterministically instead of depending on feed order.
        packs.sort(function (a, b) {
          return a.btu !== b.btu ? a.btu - b.btu : a.price - b.price;
        });
        return packs;
      });

    return packsPromise;
  }

  function adequatePacks(packs, requiredBTU) {
    return packs.filter(function (pack) {
      // Water-cooled units live in the full pack list (browse-all table) but
      // are never eligible for a recommendation, at any load, on any surface.
      return pack.btu >= requiredBTU && pack.cooling !== "water-cooled";
    });
  }

  /*
   * The set of packs eligible to be shown for this load, on ANY surface
   * (headline, cheaper alternate, also-fits). Water-cooled units never make
   * it into `packs` at all (excluded at parse time — see toPack). Remote
   * units are only included here when NO standard air-cooled pack covers
   * the load, so a remote unit can't appear as a "cheaper option" sitting
   * next to an available standard one.
   */
  function eligiblePool(packs, requiredBTU) {
    var adequate = adequatePacks(packs, requiredBTU);
    var standard = adequate.filter(function (pack) {
      return pack.tier !== "remote";
    });
    return standard.length ? standard : adequate;
  }

  /*
   * Headline pick: the smallest Pro-Line (Micro Matic) pack that covers the
   * load — Sean's preferred brand. Only falls back to another vendor when
   * Pro-Line has no pack big enough. Never picks from outside eligiblePool,
   * so this is also where the standard-before-remote rule takes effect.
   */
  function pickRecommendation(packs, requiredBTU) {
    var pool = eligiblePool(packs, requiredBTU);
    if (!pool.length) {
      return null;
    }

    var preferred = pool.filter(function (pack) {
      return pack.vendor === PREFERRED_VENDOR;
    });

    return preferred.length ? preferred[0] : pool[0];
  }

  /*
   * A bigger pack is sometimes cheaper than the smallest one that fits (the
   * Chill-Max units undercut the comparable Pro-Line packs). Surface the
   * cheapest of those rather than letting a customer overpay by default.
   */
  function cheaperAlternate(packs, requiredBTU, chosen) {
    var cheaper = eligiblePool(packs, requiredBTU).filter(function (pack) {
      return pack.handle !== chosen.handle && pack.price < chosen.price;
    });

    if (!cheaper.length) {
      return null;
    }

    return cheaper.reduce(function (best, pack) {
      return pack.price < best.price ? pack : best;
    }, cheaper[0]);
  }

  /* Remaining packs that fit, excluding ones already shown above. */
  function otherOptions(packs, requiredBTU, chosen, alternate) {
    return eligiblePool(packs, requiredBTU)
      .filter(function (pack) {
        if (pack.handle === chosen.handle) {
          return false;
        }
        return !alternate || pack.handle !== alternate.handle;
      })
      .slice(0, 2);
  }

  function element(tag, className, text) {
    var node = document.createElement(tag);
    if (className) {
      node.className = className;
    }
    if (text) {
      node.textContent = text;
    }
    return node;
  }

  function link(href, className, text) {
    var node = element("a", className, text);
    node.href = href;
    return node;
  }

  /* Product card: image, linked title, price, and a direct View Product CTA. */
  function buildCard(pack) {
    var card = element("div", "mm-rec-card");

    if (pack.image) {
      var imageLink = link(productUrl(pack.handle), "mm-rec-thumb");
      var image = document.createElement("img");
      image.src = pack.image;
      image.alt = "";
      image.loading = "lazy";
      // If the image 404s, some browsers render the alt text visibly in its
      // place — drop the whole thumbnail instead of showing broken-image text.
      image.onerror = function () {
        imageLink.remove();
      };
      imageLink.appendChild(image);
      card.appendChild(imageLink);
    }

    var body = element("div", "mm-rec-body");
    body.appendChild(link(productUrl(pack.handle), "mm-rec-title", pack.title));

    var specParts = [formatNumber(pack.btu) + " BTU/hr"];
    if (pack.hp) {
      specParts.push(pack.hp);
    }
    if (pack.pumps) {
      specParts.push(pack.pumps + (pack.pumps === 1 ? " pump" : " pumps"));
    }
    body.appendChild(element("div", "mm-rec-specs", specParts.join("  ·  ")));

    body.appendChild(element("div", "mm-rec-price", formatMoney(pack.price)));

    var cta = link(productUrl(pack.handle), "mm-rec-cta", "View Product →");
    body.appendChild(cta);

    body.appendChild(
      element(
        "div",
        "mm-rec-note",
        "Ships LTL freight — cannot ship UPS Ground. Call or email for a freight quote."
      )
    );

    card.appendChild(body);
    return card;
  }

  function buildCheaperAlternate(pack) {
    var wrap = element("div", "mm-rec-cheaper");
    wrap.appendChild(
      element("div", "mm-rec-cheaper-label", "Lower-cost option that also fits")
    );

    var row = element("div", "mm-rec-cheaper-row");
    row.appendChild(link(productUrl(pack.handle), "mm-rec-cheaper-title", pack.title));
    row.appendChild(
      element(
        "span",
        "mm-rec-cheaper-meta",
        formatNumber(pack.btu) + " BTU · " + formatMoney(pack.price)
      )
    );
    wrap.appendChild(row);

    return wrap;
  }

  function buildAlternates(packs) {
    if (!packs.length) {
      return null;
    }

    var wrap = element("div", "mm-rec-alts");
    wrap.appendChild(element("span", "mm-rec-alts-label", "Also fits this load:"));

    packs.forEach(function (pack, index) {
      if (index > 0) {
        wrap.appendChild(document.createTextNode("  ·  "));
      }
      var alt = link(
        productUrl(pack.handle),
        "mm-rec-alt",
        pack.title + " (" + formatNumber(pack.btu) + " BTU)"
      );
      wrap.appendChild(alt);
    });

    return wrap;
  }

  function renderOversized(box, requiredBTU, packs) {
    box.innerHTML = "";
    box.appendChild(element("div", "mm-rec-label", "Beyond a single standard pack"));

    var largest = packs.length ? packs[packs.length - 1].btu : null;
    box.appendChild(
      element(
        "p",
        "mm-rec-copy",
        "At " +
          formatNumber(requiredBTU) +
          " BTU/hr this system is past the largest single power pack we stock" +
          (largest ? " (" + formatNumber(largest) + " BTU)" : "") +
          ". That usually means multiple packs or a remote system. Send us the layout and we'll size it."
      )
    );

    var actions = element("div", "mm-rec-actions");
    actions.appendChild(link(CONTACT_URL, "mm-rec-cta", "Get Sizing Help →"));
    actions.appendChild(link(COLLECTION_URL, "mm-rec-alt", "Browse all glycol chillers"));
    box.appendChild(actions);
  }

  function renderLoading(box) {
    box.innerHTML = "";
    box.appendChild(element("p", "mm-rec-copy", "Finding the right power pack…"));
  }

  function renderRecommendation(box, requiredBTU, packs) {
    var pack = pickRecommendation(packs, requiredBTU);

    if (!pack) {
      renderOversized(box, requiredBTU, packs);
      return;
    }

    var alternate = cheaperAlternate(packs, requiredBTU, pack);

    box.innerHTML = "";
    box.appendChild(element("div", "mm-rec-label", "Recommended Power Pack"));
    box.appendChild(buildCard(pack));

    var headroom = pack.btu - requiredBTU;
    box.appendChild(
      element(
        "div",
        "mm-rec-fit",
        "Covers your " +
          formatNumber(requiredBTU) +
          " BTU/hr load with " +
          formatNumber(headroom) +
          " BTU/hr to spare."
      )
    );

    if (alternate) {
      box.appendChild(buildCheaperAlternate(alternate));
    }

    var alts = buildAlternates(otherOptions(packs, requiredBTU, pack, alternate));
    if (alts) {
      box.appendChild(alts);
    }
  }

  /* One card in the full browse-all-tiers table — same fields as buildCard,
   * plus cooling/remote so every variant is distinguishable at a glance. */
  function buildTierCard(pack) {
    var card = element("div", "mm-tier-card");

    if (pack.image) {
      var imageLink = link(productUrl(pack.handle), "mm-tier-thumb");
      var image = document.createElement("img");
      image.src = pack.image;
      image.alt = "";
      image.loading = "lazy";
      image.onerror = function () {
        imageLink.remove();
      };
      imageLink.appendChild(image);
      card.appendChild(imageLink);
    }

    var body = element("div", "mm-tier-body");
    body.appendChild(link(productUrl(pack.handle), "mm-tier-title", pack.title));

    var specParts = [];
    if (pack.hp) {
      specParts.push(pack.hp);
    }
    if (pack.pumps) {
      specParts.push(pack.pumps + (pack.pumps === 1 ? " pump" : " pumps"));
    }
    specParts.push(pack.cooling === "water-cooled" ? "Water-Cooled" : "Air-Cooled");
    if (pack.tier === "remote") {
      specParts.push("Remote");
    }
    body.appendChild(element("div", "mm-tier-specs", specParts.join("  ·  ")));

    body.appendChild(element("div", "mm-tier-price", formatMoney(pack.price)));
    body.appendChild(link(productUrl(pack.handle), "mm-tier-cta", "View Product →"));

    card.appendChild(body);
    return card;
  }

  /*
   * Full catalog browse table shown below the calculator's recommendation —
   * every power pack we currently sell, grouped by BTU rating, including
   * remote and water-cooled variants (those are excluded from the
   * *recommendation* logic above, but this table is informational browsing,
   * not a recommendation, so nothing is hidden here). Renders once when the
   * product feed resolves; it doesn't depend on the calculator's inputs.
   */
  function renderAllTiers(box, packs) {
    if (!box) {
      return;
    }

    box.innerHTML = "";
    if (!packs.length) {
      return;
    }

    var byBTU = {};
    var order = [];
    packs.forEach(function (pack) {
      if (!byBTU[pack.btu]) {
        byBTU[pack.btu] = [];
        order.push(pack.btu);
      }
      byBTU[pack.btu].push(pack);
    });
    order.sort(function (a, b) {
      return a - b;
    });

    order.forEach(function (btu) {
      var group = element("div", "mm-tier-group");
      group.appendChild(
        element("h3", "mm-tier-heading", formatNumber(btu) + " BTU Power Packs")
      );

      var grid = element("div", "mm-tier-grid");
      byBTU[btu]
        .sort(function (a, b) {
          return a.price - b.price;
        })
        .forEach(function (pack) {
          grid.appendChild(buildTierCard(pack));
        });
      group.appendChild(grid);

      box.appendChild(group);
    });
  }

  function initBTUCalculator() {
    var faucetsInput = document.getElementById("faucets");
    var lengthInput = document.getElementById("length");
    var sizeSelect = document.getElementById("size");
    var result = document.getElementById("result");
    var recommendationBox = document.getElementById("recommendationBox");
    var allTiersBox = document.getElementById("allTiersBox");

    if (!faucetsInput || !lengthInput || !sizeSelect || !result) {
      return;
    }

    // Kick the fetch off immediately so it's usually already resolved by the
    // time someone finishes typing their first input.
    getPacks().then(function (packs) {
      renderAllTiers(allTiersBox, packs);
    });

    var latestRequiredBTU = 0;

    function calculateBTU() {
      var faucets = parseFloat(faucetsInput.value) || 0;
      var length = parseFloat(lengthInput.value) || 0;

      var selectedOption = sizeSelect.options[sizeSelect.selectedIndex];
      var trunkBTUperFt = parseFloat(selectedOption.getAttribute("data-btu")) || 0;

      var requiredBTU = (faucets * BTU_PER_FAUCET + length * trunkBTUperFt) * SAFETY_FACTOR;
      latestRequiredBTU = requiredBTU;

      result.textContent = formatNumber(requiredBTU) + " BTU/hr";

      if (!recommendationBox) {
        return;
      }

      // Nothing entered yet — prompt instead of recommending the smallest pack.
      if (requiredBTU <= 0) {
        recommendationBox.innerHTML = "";
        recommendationBox.appendChild(
          element(
            "p",
            "mm-rec-copy",
            "Enter your faucet count and trunkline run above to see the right glycol power pack for your system."
          )
        );
        return;
      }

      renderLoading(recommendationBox);

      getPacks().then(function (packs) {
        // Guard against a slower earlier fetch overwriting a newer calculation.
        if (requiredBTU === latestRequiredBTU) {
          renderRecommendation(recommendationBox, requiredBTU, packs);
        }
      });
    }

    faucetsInput.addEventListener("input", calculateBTU);
    lengthInput.addEventListener("input", calculateBTU);
    sizeSelect.addEventListener("change", calculateBTU);

    calculateBTU();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initBTUCalculator);
  } else {
    initBTUCalculator();
  }
})();